import { TemplateResult } from "lit-html";
import JrModal from "@libs/modal";
import { addMySearches, subscribeJobseeker } from "@services/xhr-service";
import { focusHandler, isValidEmail } from "@utils/index";
import { findProvider } from "@services/link-to-inbox-service";
import { pushEvent } from "@services/gtm-service";
import { getWhat, getWhere } from "@services/search-service";
import { emitAngularEvent } from "@services/event-service";
import { searchUrlFormatter } from "@utils/formatters";
import { activateExitIntentPopup } from "@services/exit-intent-service";
import { JtagService } from "@services/jtag-service";
import { getDomain } from "@services/email-service";
import { promptPushnamiNotification } from "@services/notification-service";
import ModalProposalTemplate from "../templates";
import {
  composeDescription,
  updateDescription,
  proposalConfigs,
  renderTopToolsLinkMenuDesktopComponent,
  renderTopToolsLinkMenuMobileComponent,
  renderMySearchesComponent,
  renderStatementDescriptionTemplate
} from "../configs";

enum InputType {
  WHAT = 'what',
  WHERE = 'where'
}

export default class SingleConfirmationProposalModal extends JrModal {
  private loading: boolean;
  private config: ProposalConfigs;
  private formData: IProposalFormData;
  private description: string;
  private formSubmitted: boolean = false;
  private captchaEnabled: boolean;
  private captchaSiteKey: string;
  private captchaResponse: string = '';
  private captchaInstance: number = -1;
  private socialCaptchaInstance: number = -1;
  private socialCaptchaResponse: string = '';
  private mySearches: Array<IMySearch> = [];
  private linkToInboxProviderUrl: string = '';
  private socialSubscriptionError: boolean = false;
  private socialButtonClassName: string = '';
  private jobseekerFirstName: string = '';
  private jobseekerLastName: string = '';
  private modalState: 'proposal' | 'welcome' | 'mySearches' | 'error' = 'proposal';

  constructor(
    readonly customModalClass: string,
    readonly wrapperClass: string,
    readonly jrConfigGlobal: JrConfigGlobal,
    readonly source: string = 'auto'
  ) {
    super(customModalClass, wrapperClass, jrConfigGlobal);

    this.config = proposalConfigs(jrConfigGlobal);
    this.description = composeDescription(jrConfigGlobal);
    this.loading = false;
    this.formData = this.getFormData(jrConfigGlobal);
    this.captchaEnabled = this.jrConfigGlobal.captcha.enabled;
    this.captchaSiteKey = this.jrConfigGlobal.captcha.siteKey;
  }

  handleWhatInput = (event: Event): void => {
    const eventValue = (<HTMLInputElement>event.target).value.trim();

    if (eventValue) {
      this.setDOMElement(eventValue, InputType.WHAT);
    }
  }

  handleWhereInput = (value: string): void => this.setDOMElement(value, InputType.WHERE);

  handleClearWhatInput = (): void => this.setDOMElement('', InputType.WHAT);

  handleEmailChange = (value: string): void => {
    this.setEmail(value);

    this.render();
  }

  handleSubmit = async (event: Event) => {
    event.preventDefault();

    const { what, where, email }: IProposalModalFormElements = <IProposalModalFormElements>(<HTMLFormElement>event.target);

    this.setWhat(what?.value || '');
    this.setWhere(where?.value || '');
    this.setEmail(email.value);
    this.setFormSubmitted(true);

    this.render();

    if (this.isValidform()) {
      const search = { what: what?.value || '', where: where?.value || '' };
      this.setMySearches({ search, url: searchUrlFormatter(search) });

      if (this.captchaEnabled && this.captchaInstance !== -1) {
        grecaptcha.ready(() => {
          grecaptcha.execute(this.captchaInstance);
        })
      } else {
        await this.registerJobseeker();
      }
    }
  }

  handleMySearchesSubmit = async (event: Event) => {
    this.modalState = 'mySearches';
    event.preventDefault();
    const formElements: ProposalMySearchFormElements = <ProposalMySearchFormElements>(<HTMLFormElement>event.target);
    const formData: IAddMySearches = {
      mySearches: [{
        what: formElements.what1.value,
        where: formElements.where1.value
      }, {
        what: formElements.what2.value,
        where: formElements.where2.value
      }],
      email: this.formData.email?.value || ''
    };

    try {
      const response = await addMySearches(formData);

      if (this.isResponseSuccessful(response)) {
        formData.mySearches.forEach(search => {
          if (search.what || search.where) {
            this.setMySearches({ search, url: searchUrlFormatter(search) });
          }
        });

        if (this.jrConfigGlobal.feature.singleOptInWelcomePopupEnabled) {
          this.modalState = 'welcome';
          this.render();
        } else {
          this.close();
        }
      } else {
        this.modalState = 'error';
        this.render();
      }
    } catch (_) {
      this.modalState = 'error';
      this.render();
    }
  }

  handleOneSearchSubmit = async (): Promise<void> => {
    this.modalState = 'mySearches';
    const data: IAddMySearches = {
      mySearches: [{
        what: this.formData.what.value,
        where: this.formData.where.value
      }],
      email: this.formData.email?.value || ''
    };

    try {
      const response = await addMySearches(data);

      if (this.isResponseSuccessful(response)) {
        if (this.jrConfigGlobal.feature.singleOptInWelcomePopupEnabled) {
          this.modalState = 'welcome';
          this.render();
        } else {
          this.close();
        }
      } else {
        this.modalState = 'error';
        this.render();
      }
    } catch (_) {
      this.modalState = 'error';
      this.render();
    }
  }

  handleSocialSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    this.setLoading(true);
    this.render();

    if (this.isValidSocialForm()) {
      const { className } = (<HTMLElement>event.target);
      this.setSocialButtonClassName(className);

      const search = { what: this.formData.what.value, where: this.formData.where.value };
      this.setMySearches({ search, url: searchUrlFormatter(search) });

      if (this.captchaEnabled && this.socialCaptchaInstance !== -1) {
        grecaptcha.ready(() => grecaptcha.execute(this.socialCaptchaInstance))
      } else {
        await this.handleSocialLogin();
      }
    }
  }

  handleAfterClose = (): void => {
    switch (this.modalState) {
      case 'welcome':
      case 'mySearches': {
        if (this.jrConfigGlobal.feature.singleOptInNoRefreshEnabled) {
          this.handleSingleOptinNoRefreshFlow();
        } else {
          this.handleSingleOptinRefreshFlow();
        }
        break;
      }
      case 'proposal': {
        if (this.jrConfigGlobal.feature.exitIntentPopupEnabled && this.jrConfigGlobal.isDesktop) {
          activateExitIntentPopup()
        }
        if (this.jrConfigGlobal.pushNotifications.active) {
          promptPushnamiNotification();
        }
        break;
      }

      default:
        break;
    }
  }

  getContent = async (): Promise<TemplateResult> => ModalProposalTemplate(this.config, this.getModel());

  openedCallback = (): void => {
    const modalCaptchaContainerElement = document.getElementById("modal-captcha-container");
    const modalSocialCaptchaContainerElement = document.getElementById("modal-social-captcha-container");
    if (this.captchaEnabled && modalCaptchaContainerElement) {
      const interval = setInterval(() => {
        if (window.grecaptcha) {
          grecaptcha.ready(() => {
            this.initializeCaptchaInstance();
            if (this.jrConfigGlobal.socialLogin.enabled &&
              this.jrConfigGlobal.socialLogin.insidePopupEnabled &&
              modalSocialCaptchaContainerElement) {
              this.initializeSocialCaptchaInstance();
            }
          })
          clearInterval(interval);
        }
      }, 100);
    }
  }

  private initializeCaptchaInstance = () => {
    this.captchaInstance = window.grecaptcha.render('modal-captcha-container', {
      sitekey: this.captchaSiteKey, size: "invisible", badge: 'inline', callback: async (response) => {
        if (response) {
          this.setCaptchaResponse(response);
          await this.registerJobseeker();
        } else {
          this.handleCaptchaError();
        }
      }, 'error-callback': this.handleCaptchaError, 'expired-callback': this.handleCaptchaError
    });
  }

  private initializeSocialCaptchaInstance = () => {
    this.socialCaptchaInstance = window.grecaptcha.render('modal-social-captcha-container', {
      sitekey: this.captchaSiteKey, size: "invisible", badge: 'inline', callback: (response) => {
        if (response) {
          this.setSocialCaptchaResponse(response);
          this.handleSocialLogin();
        } else {
          this.handleSocialCaptchaError();
        }
      }, 'error-callback': this.handleSocialCaptchaError, 'expired-callback': this.handleSocialCaptchaError
    });
  }

  private getModel = (): IProposalModel => ({
    handleSubmit: this.handleSubmit,
    handleSocialSubmit: this.handleSocialSubmit,
    handleMySearchesSubmit: this.handleMySearchesSubmit,
    handleOneSearchSubmit: this.handleOneSearchSubmit,
    handleWhatInput: this.handleWhatInput,
    handleWhereInput: this.handleWhereInput,
    handleClearWhatInput: this.handleClearWhatInput,
    handleEmailChange: this.handleEmailChange,
    close: this.close,
    loading: this.loading,
    formData: this.formData,
    description: this.description,
    formSubmitted: this.formSubmitted,
    modalState: this.modalState,
    linkToInboxProviderUrl: this.linkToInboxProviderUrl,
    socialSubscriptionError: this.socialSubscriptionError,
  });

  private setLinkToInboxProviderUrl = (url: string): void => {
    this.linkToInboxProviderUrl = url;
  }

  private trackWhereByIp = (): void => {
    if (this.jrConfigGlobal.feature.locationFieldInModalPopupEnabled && !this.config.isEmptySearch) {
      JtagService.trackCustomEvent('subscription_modal_where_by_ip_input_value', {
        event_label: this.formData.where.value ? 'filled' : 'empty'
      });
    }
  }

  private setMySearches = (mySearch: IMySearch): void => {
    this.mySearches.push(mySearch);
  }

  private setLoading = (loading: boolean): void => {
    this.loading = loading;
  }

  private setFormSubmitted = (isSubmitted: boolean): void => {
    this.formSubmitted = isSubmitted;
  }

  private setCaptchaResponse = (captchaResponse: string): void => {
    this.captchaResponse = captchaResponse;
  }

  private getFormData = (jrConfigGlobal: JrConfigGlobal = window.jrConfigGlobal): IProposalFormData => {
    const {
      search: {
        where: { resolvedByIp },
        radius: { value: radiusValue }
      }
    } = jrConfigGlobal;

    return {
      what: {
        value: getWhat(jrConfigGlobal),
      },
      where: {
        value: resolvedByIp || getWhere(jrConfigGlobal),
        isResolvedByIp: resolvedByIp.length > 0
      },
      email: {
        value: '',
        isValid: false
      },
      radius: {
        value: radiusValue
      }
    }
  }

  private setDescription = (what: string, where: string): void => {
    this.description = updateDescription(what, where);
  }

  private setWhat = (eventValue: string): void => {
    this.formData.what.value = eventValue;
  }

  private setWhere = (eventValue: string): void => {
    this.formData.where.value = eventValue;
  }

  private setEmail = (value: string): void => {
    this.formData.email = {
      value,
      isValid: isValidEmail(value),
    }
  }

  private setDOMElement = (value: string, inputType: string): void => {
    if (inputType === InputType.WHAT) {
      this.setWhat(value);
    }
    if (inputType === InputType.WHERE) {
      this.setWhere(value);
    }

    if (!this.jrConfigGlobal.page.isJobPreviewPage) {
      this.setDescription(this.formData.what.value, this.formData.where.value);
      setTimeout(() => {
        renderStatementDescriptionTemplate(this.description);
      });
    }

    this.render();
  }

  private setJobseekerFirstName = (value: string): void => {
    this.jobseekerFirstName = value;
  }

  private setJobseekerLastName = (value: string): void => {
    this.jobseekerLastName = value;
  }

  private setSocialCaptchaResponse = (socialCaptchaResponse: string): void => {
    this.socialCaptchaResponse = socialCaptchaResponse;
  }

  private setSocialButtonClassName = (socialButtonClassName: string): void => {
    this.socialButtonClassName = socialButtonClassName;
  }

  private isValidform = (): boolean =>
    !!this.formData.email?.isValid
    && (this.formData.what.value.length > 0 || this.formData.where.value.length > 0);

  private isValidSocialForm = (): boolean =>
    (this.formData.what.value.length > 0 || this.formData.where.value.length > 0);

  private isResponseSuccessful = (response: Response): boolean => response.status >= 200 && response.status < 300;
  private isResponseConflict = (response: Response): boolean => response.status === 409;

  private sendData = async (data: ISubscribe) => {
    try {
      const response: Response = await subscribeJobseeker(data);
      const jobseekerCreated = this.isResponseSuccessful(response);
      const jobseekerConflict = this.isResponseConflict(response);

      pushEvent({
        event: 'jobseekerCreate',
        jobseekerCreated,
        subscriberUid: response.headers.get('subscriberUid'),
        subscriptionConversionValue: parseFloat(response.headers.get('subscriptionConversionValue') || '0')
      });

      if (jobseekerCreated || jobseekerConflict) {
        this.handleAfterSubscription();
      } else {
        this.setLoading(false);
        this.modalState = 'error';
        this.render();
      }
    } catch (error) {
      pushEvent({
        event: 'jobseekerCreate',
        jobseekerCreated: false,
        subscriberUid: undefined,
        subscriptionConversionValue: undefined
      });

      this.setLoading(false);
      this.modalState = 'error';
      this.render();
    }
  }

  private handleLinkedinLogin = () => {
    // TODO
    // handleLinkedinLogin
  }

  private handleGoogleLogin = async (): Promise<void> => {
    try {
      const googleUser = await window.auth2.signIn({ scope: 'profile email' });
      const userProfile = googleUser.getBasicProfile();

      const email = userProfile.getEmail() || '';
      const firstName = userProfile.getGivenName() || '';
      const lastName = userProfile.getFamilyName() || '';

      this.setEmail(email);
      this.setJobseekerLastName(lastName);
      this.setJobseekerFirstName(firstName);

      await this.handleAfterSocialSubscription('google');
    } catch (err) {
      this.handleSocialSubscriptionError();
    }
  }

  private handleFacebookLogin = (): void => {
    try {
      window.FB.login(
        (loginResponse) => {
          if (loginResponse.status === 'connected') {
            window.FB.api('/me', { fields: ['email', 'first_name', 'last_name'] }, async (apiResponse) => {
              const {
                email = '',
                first_name: firstName = '',
                last_name: lastName = '',
              } = apiResponse;

              this.setEmail(email);
              this.setJobseekerLastName(lastName);
              this.setJobseekerFirstName(firstName);

              await this.handleAfterSocialSubscription('facebook');
            });
          } else {
            this.handleSocialSubscriptionError();
          }
        },
        {
          scope: 'public_profile, email',
          auth_type: 'rerequest',
        }
      );
    } catch (err) {
      this.handleSocialSubscriptionError();
    }
  }

  private handleSocialSubscriptionError = (): void => {
    this.setLoading(false);
    this.socialSubscriptionError = true;
    this.modalState = 'error';
    this.render();
  }

  private hideNotificationBox() {
    if (this.jrConfigGlobal.feature.notificationAlertBoxEnabled) {
      const notificationBox = <HTMLElement>document.querySelector('.js-notification-alert');
      notificationBox.classList.add('hide');
    }
  }

  private handleAfterSubscription() {
    this.setLoading(false);
    this.modalState = 'welcome';

    JtagService.trackSubscription(`modal_${this.source}`, getDomain(this.formData.email?.value || '') || '');
    JtagService.trackConfirmation('single_opt_in');

    const linkToInboxProviders: LinkToInboxProvider[] = findProvider(this.formData.email?.value || '');

    if (linkToInboxProviders.length === 0) {
      this.modalState = 'mySearches';
      this.render();
      focusHandler('.js-mysearch-what1');
    } else if (this.jrConfigGlobal.feature.singleOptInWelcomePopupEnabled) {
      this.setLinkToInboxProviderUrl(linkToInboxProviders[0].url);
      this.render();
    } else {
      this.close();
    }
  }

  private handleAfterSocialSubscription = async (socialSource: string): Promise<void> => {
    try {
      if (this.isValidform()) {
        const data: ISubscribe = {
          source: socialSource,
          referrer: 'pop-up',
          lastname: this.jobseekerLastName,
          firstname: this.jobseekerFirstName,
          what: this.formData.what.value,
          where: this.formData.where.value,
          email: this.formData.email?.value || '',
          radius: this.formData.radius?.value || '',
          captchaResponse: this.socialCaptchaResponse
        };

        const response: Response = await subscribeJobseeker(data);
        const jobseekerCreated = this.isResponseSuccessful(response);
        const jobseekerConflict = this.isResponseConflict(response);
        const redirectUrl = response.headers.get('activateUrl');

        if (jobseekerCreated) {
          JtagService.trackSubscription(`signup_modal_${socialSource}`, getDomain(this.formData.email?.value || '') || '');

          if (redirectUrl && (jobseekerCreated || jobseekerConflict)) {
            window.location.replace(redirectUrl);
          }
        }
      } else {
        this.handleSocialSubscriptionError();
      }
    } catch (err) {
      this.handleSocialSubscriptionError();
    }
  }

  private handleSingleOptinRefreshFlow() {
    switch (this.source) {
      case 'auto':
        setTimeout(() => {
          window.location.reload();
        });
        break;
      case 'jppApplyBtn':
      case 'jppShowMoreBtn':
        emitAngularEvent('subscriptionSentWhenApplyShowMoreFlowIsEnabled', { source: this.source, shouldReload: true });
        break
      default:
        break;
    }
  }

  private handleSingleOptinNoRefreshFlow() {
    this.hideNotificationBox();
    window.google.accounts.id.cancel();
    renderMySearchesComponent(
      {
        mySearches: this.mySearches,
        messages: this.jrConfigGlobal.messages
      }
    )

    emitAngularEvent('singleOptInSubscriptionConfirmed', { email: this.formData.email?.value });

    if (this.jrConfigGlobal.isDesktop) {
      renderTopToolsLinkMenuDesktopComponent(
        {
          jobseekerEmail: this.formData.email?.value,
          messages: this.jrConfigGlobal.messages
        });
    } else {
      renderTopToolsLinkMenuMobileComponent(
        {
          messages: this.jrConfigGlobal.messages,
        });
    }

    if (this.source === 'jppApplyBtn' || this.source === 'jppShowMoreBtn') {
      emitAngularEvent('subscriptionSentWhenApplyShowMoreFlowIsEnabled', { source: this.source, shouldReload: false });
    }
  }

  private handleCaptchaError() {
    window.grecaptcha.reset(this.captchaInstance);
    this.setLoading(false);
    this.render();
  }

  private async registerJobseeker() {
    this.setLoading(true);
    this.render();

    const formData: ISubscribe = {
      what: this.formData.what.value,
      where: this.formData.where.value,
      email: this.formData.email?.value || '',
      radius: this.formData.radius?.value || '',
      captchaResponse: this.captchaResponse
    };
    this.trackWhereByIp();

    await this.sendData(formData);
  }

  private handleSocialCaptchaError() {
    window.grecaptcha.reset(this.socialCaptchaInstance);
    this.setLoading(false);
    this.render();
  }

  private handleSocialLogin = async (): Promise<void> => {
    switch (true) {
      case this.socialButtonClassName.includes("ts-facebook"):
        this.handleFacebookLogin();

        break;
      case this.socialButtonClassName.includes("ts-linkedin"):
        this.handleLinkedinLogin();

        break;
      case this.socialButtonClassName.includes("ts-google"):
        await this.handleGoogleLogin();

        break;

      default:
        break;
    }
  }
}
